import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';
import { YButton } from '../../../ui/YButton';
import api from '../../../utils/api';
import { toolkitService } from '../../toolkit/services/toolkitService';
import { buildPlanViewModel } from '../../toolkit/pages/plantingPlanDisplay';
import {
  buildFieldEditionExportPayload,
  formatExportWindowLabel,
  formatUserDisplayName,
} from '../../toolkit/pages/plantingExportUtils';
import {
  CLUB_SEED_DETAIL_ERROR,
  CLUB_SEED_EXPORT,
  CLUB_SEED_EXPORT_ERROR,
  CLUB_SEED_EXPORTING,
  sharedPlanPreviewRequest,
} from '../clubSharedPlans';

export function ClubSharedPlanDetail({ plan, clubName = '', variant = 'embed' }) {
  const [status, setStatus] = useState('loading');
  const [model, setModel] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!plan) return undefined;
    let cancelled = false;
    setStatus('loading');
    setModel(null);
    setError('');
    toolkitService.getPlanPreview(sharedPlanPreviewRequest(plan))
      .then((payload) => {
        if (cancelled) return;
        const next = buildPlanViewModel(payload, {
          zone: plan.usda_zone_code,
        });
        setModel(next);
        setStatus(next ? 'ready' : 'error');
      })
      .catch(() => {
        if (cancelled) return;
        setModel(null);
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [plan]);

  const handleExport = async () => {
    if (!plan || exporting) return;
    setExporting(true);
    setError('');
    try {
      const profileRes = await api.get('/profiles/me/');
      const payload = await buildFieldEditionExportPayload({
        group: plan,
        userDisplayName: formatUserDisplayName(profileRes?.data || {}),
        plots: [],
        clubName,
        zoneLabel: plan.usda_zone_code ? String(plan.usda_zone_code).toUpperCase() : '',
        soilTypeLabel: String(plan.soil_type || '').replace(/_/g, ' '),
        soilDrainageLabel: String(plan.soil_drainage || '').replace(/_/g, ' '),
        loadPlan: toolkitService.getPlanPreview,
      });
      payload.clubName = clubName || payload.clubName;
      payload.isClubGroup = Boolean(clubName);
      const { downloadPlantingEstimatePdf } = await import(
        '../../toolkit/pages/downloadPlantingEstimatePdf'
      );
      await downloadPlantingEstimatePdf(payload);
    } catch (err) {
      setError(CLUB_SEED_EXPORT_ERROR);
    } finally {
      setExporting(false);
    }
  };

  const windowLabel = formatExportWindowLabel(plan?.window_start, plan?.window_end);
  const prefix = variant === 'board' ? 'club-seed-detail--board' : 'club-seed-detail';

  return (
    <div
      id={`club-shared-plan-detail-${plan.id}`}
      className={`club-seed-detail ${prefix}`}
      data-testid={`club-shared-plan-detail-${plan.id}`}
    >
      {status === 'loading' ? (
        <div className="club-seed-detail__status" data-testid={`club-shared-plan-loading-${plan.id}`}>
          <Spinner animation="border" size="sm" />
          <span>Reading the blend…</span>
        </div>
      ) : null}
      {status === 'error' ? (
        <p className="club-seed-detail__error" data-testid={`club-shared-plan-load-error-${plan.id}`}>
          {CLUB_SEED_DETAIL_ERROR}
        </p>
      ) : null}
      {status === 'ready' && model ? (
        <>
          {windowLabel ? (
            <p className="club-seed-detail__window" data-testid={`club-shared-plan-window-${plan.id}`}>
              {`Window · ${windowLabel}`}
            </p>
          ) : null}
          <p className="club-seed-detail__blend" data-testid={`club-shared-plan-blend-${plan.id}`}>
            {model.recName}
          </p>
          <div className="club-seed-detail__cart" data-testid={`club-shared-plan-cart-${plan.id}`}>
            <div className="club-seed-detail__cart-kicker">WHAT TO BUY</div>
            {(model.cart || []).map((item) => (
              <div key={`${item.name}-${item.rate}`} className="club-seed-detail__cart-row">
                <div className="club-seed-detail__cart-copy">
                  <span className="club-seed-detail__cart-name">{item.name}</span>
                  <span className="club-seed-detail__cart-meta">{item.rate} · {item.bags}</span>
                </div>
                <span className="club-seed-detail__cart-qty">{item.qty}</span>
              </div>
            ))}
            {model.totalLbs ? (
              <p className="club-seed-detail__total">{`${model.totalLbs} lbs total`}</p>
            ) : null}
          </div>
        </>
      ) : null}
      {error ? (
        <p className="club-seed-detail__error" role="alert">{error}</p>
      ) : null}
      <YButton
        type="button"
        variant="secondary"
        size="sm"
        className="club-seed-detail__export"
        data-testid={`club-shared-plan-export-${plan.id}`}
        disabled={exporting || !plan}
        onClick={handleExport}
      >
        {exporting ? CLUB_SEED_EXPORTING : CLUB_SEED_EXPORT}
      </YButton>
    </div>
  );
}

ClubSharedPlanDetail.propTypes = {
  plan: PropTypes.object,
  clubName: PropTypes.string,
  variant: PropTypes.oneOf(['embed', 'board']),
};

export default ClubSharedPlanDetail;
